import * as React from 'react';

/**
 * Cross-component guard that lets the manual New Order wizard tell the
 * Bolt/Wolt takeover popup "a staff member is mid-order — hold off".
 *
 * Two independent signals live here so the two components (which sit in
 * different parts of the tree, both under AppLayout) can talk without
 * prop-drilling:
 *
 *   - orderEntryActive: raised (ref-counted) while the New Order wizard
 *     is open. The takeover popup reads it to decide whether to hold an
 *     incoming order back instead of taking over the screen.
 *   - heldCount: set by the takeover popup to the number of orders it is
 *     currently holding. The wizard reads it to show a small "order
 *     waiting" chip so staff know to wrap up.
 *
 * Ref-counted entry so two stacked dialogs (edit-over-new, etc.) never
 * let one's close prematurely drop the guard.
 *
 * SAFETY: this only ever DELAYS the popup, and the popup enforces its own
 * hard cap (see NewExternalOrderAlert) so an order can never be held long
 * enough to miss Bolt's accept deadline / trigger a venue auto-pause.
 */
type Listener = () => void;

let entryRefCount = 0;
let heldCount = 0;
const listeners = new Set<Listener>();

const notify = () => {
  listeners.forEach((l) => l());
};

export const orderEntryGuard = {
  enterOrderEntry() {
    entryRefCount += 1;
    notify();
  },
  leaveOrderEntry() {
    entryRefCount = Math.max(0, entryRefCount - 1);
    notify();
  },
  isOrderEntryActive() {
    return entryRefCount > 0;
  },
  setHeldCount(n: number) {
    const next = Math.max(0, n);
    if (next !== heldCount) {
      heldCount = next;
      notify();
    }
  },
  getHeldCount() {
    return heldCount;
  },
  subscribe(l: Listener) {
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  },
};

/**
 * Subscribe a component to the guard. Re-renders on any change and
 * returns the current snapshot plus the mutators the component needs.
 */
export const useOrderEntryGuard = () => {
  const [, force] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => orderEntryGuard.subscribe(force), []);
  return {
    orderEntryActive: orderEntryGuard.isOrderEntryActive(),
    heldCount: orderEntryGuard.getHeldCount(),
    enterOrderEntry: orderEntryGuard.enterOrderEntry,
    leaveOrderEntry: orderEntryGuard.leaveOrderEntry,
    setHeldCount: orderEntryGuard.setHeldCount,
  };
};
